import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { getWeightLogs, getWorkoutLogs, getNutritionLogs } from '../../services/firestoreService';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { differenceInDays, format, subDays } from 'date-fns';

export default function ProgressPage() {
  const { theme } = useTheme();
  const { user, userProfile } = useAuth();
  const [weightLogs, setWeightLogs] = useState([]);
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const [nutritionLogs, setNutritionLogs] = useState([]);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      getWeightLogs(user.uid, 90),
      getWorkoutLogs(user.uid, 30),
      getNutritionLogs(user.uid, 30),
    ]).then(([w, wo, n]) => {
      setWeightLogs(w.reverse());
      setWorkoutLogs(wo);
      setNutritionLogs(n);
    });
  }, [user]);

  // Build weekly workout count
  const last7 = [...Array(7)].map((_, i) => {
    const d = format(subDays(new Date(), 6 - i), 'yyyy-MM-dd');
    const count = workoutLogs.filter(l => l.loggedAt?.toDate && format(l.loggedAt.toDate(), 'yyyy-MM-dd') === d).length;
    return { day: format(subDays(new Date(), 6 - i), 'EEE'), count };
  });

  const goalWeight = userProfile?.goals?.targetWeight;
  const startWeight = userProfile?.currentWeight;
  const currentWeight = weightLogs[weightLogs.length - 1]?.weight;
  const totalLost = startWeight && currentWeight ? (startWeight - currentWeight).toFixed(1) : null;
  const progressPct = startWeight && goalWeight && currentWeight
    ? Math.min(100, Math.max(0, Math.round(((startWeight - currentWeight) / (startWeight - goalWeight)) * 100))) : 0;

  const daysActive = user?.metadata?.creationTime
    ? differenceInDays(new Date(), new Date(user.metadata.creationTime)) : 0;

  const statCard = (emoji, label, value, color) => (
    <div className="card" style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>{emoji}</div>
      <div style={{ color: theme.textSecondary, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: color || theme.text }}>{value}</div>
    </div>
  );

  return (
    <div style={{ padding: '2rem', maxWidth: '1000px' }} className="animate-fade">
      <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', fontWeight: 700, color: theme.text, marginBottom: '2rem' }}>
        📈 Your Progress
      </h1>

      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {statCard('📅', 'Days Active', daysActive, theme.accent)}
        {statCard('💪', 'Workouts Logged', workoutLogs.length, theme.success)}
        {statCard('🥗', 'Meals Logged', nutritionLogs.length, theme.warning)}
        {statCard('📉', 'Lbs Lost', totalLost ? `${Math.abs(totalLost)}` : '--', parseFloat(totalLost) > 0 ? theme.success : theme.danger)}
        {statCard('🎯', 'Goal Progress', `${progressPct}%`, theme.accent)}
      </div>

      {/* Goal Progress Bar */}
      {progressPct >= 0 && startWeight && goalWeight && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, color: theme.text }}>Weight Goal Journey</span>
            <span style={{ color: theme.accent, fontWeight: 700, fontFamily: "'Rajdhani', sans-serif" }}>{progressPct}% Complete</span>
          </div>
          <div style={{ height: '20px', background: theme.border, borderRadius: '10px', overflow: 'hidden', position: 'relative' }}>
            <div style={{
              height: '100%', width: `${progressPct}%`,
              background: `linear-gradient(90deg, ${theme.accent}, ${theme.success})`,
              borderRadius: '10px', transition: 'width 1.5s ease',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '8px',
            }}>
              {progressPct >= 20 && <span style={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700 }}>{progressPct}%</span>}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.8rem', color: theme.textSecondary }}>
            <span>Start: {startWeight} lbs</span>
            <span>Current: {currentWeight || startWeight} lbs</span>
            <span>Goal: {goalWeight} lbs</span>
          </div>
        </div>
      )}

      {/* Weekly Workout Chart */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, color: theme.text, marginBottom: '1rem' }}>
          💪 Workouts This Week
        </h3>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={last7}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
            <XAxis dataKey="day" tick={{ fill: theme.textSecondary, fontSize: 12 }} />
            <YAxis allowDecimals={false} tick={{ fill: theme.textSecondary, fontSize: 12 }} />
            <Tooltip contentStyle={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '8px', color: theme.text }} />
            <Bar dataKey="count" fill={theme.accent} radius={[4, 4, 0, 0]} name="Workouts" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Weight trend */}
      {weightLogs.length > 1 && (
        <div className="card">
          <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, color: theme.text, marginBottom: '1rem' }}>
            ⚖️ Weight Trend (90 Days)
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={weightLogs.map(l => ({ date: l.date, weight: l.weight }))}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
              <XAxis dataKey="date" tick={{ fill: theme.textSecondary, fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis domain={['auto', 'auto']} tick={{ fill: theme.textSecondary, fontSize: 11 }} />
              <Tooltip contentStyle={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '8px', color: theme.text }} formatter={v => [`${v} lbs`, 'Weight']} />
              <Line type="monotone" dataKey="weight" stroke={theme.accent} strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
